import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';

import { FiList } from 'react-icons/fi';

import { adminLearningPathsApi } from '@/services/adminLearningPaths';
import { PATHS } from '@/routes/paths';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableRow } from '@/components/ui/table';
import { Filter, FilterContent, FilterRow, FilterSelect } from '@/components/custom/filter';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { TiaBanner } from '@/components/custom/tia-banner';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { DashboardContent } from '@/layouts/dashboard';
import { useSession } from '@/hooks/useSession';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { isTeacherOnly } from '@/lib/teacher-profile';
import { PENSANDO } from '@/pages/dashboard/home-teacher/teacher-character';

import TrilhaPerformanceModal from './TrilhaPerformanceModal';
import TrilhaProgressModal from './TrilhaProgressModal';
import { Pagination, StatusBadge, TitleCell, TrailActions, formatDate, formatPercent } from './TrilhaStudentsShared';

// Trilha adaptativa: a API agrupa por aluno (id = id do aluno) e as trilhas dele ficam em TrilhaStudentTrails.
const GROUP_TYPE = 'learning_path_student_group';

const TABLE_HEAD = [
  { label: 'Aluno' },
  { label: 'Status', width: 150 },
  { label: '% concluído', width: 110 },
  { label: '% dominado', width: 110 },
  { label: 'Início', width: 160 },
  { label: 'Último acesso', width: 160 },
  { label: 'Ações', width: 420 }
];

const GROUP_TABLE_HEAD = [
  { label: 'Aluno' },
  { label: 'Total de trilhas', width: 140 },
  { label: 'Trilhas concluídas', width: 160 },
  { label: 'Último acesso', width: 160 },
  { label: 'Ações', width: 180 }
];

const EMPTY_FILTERS = { regionalId: '', institutionId: '', courseId: '', classroomId: '' };

export default function TrilhaStudents() {
  const { learningPathId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [trilhaName, setTrilhaName] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [progressTarget, setProgressTarget] = useState(null);
  const [performanceTarget, setPerformanceTarget] = useState(null);
  const { session } = useSession();
  const teacherOnly = isTeacherOnly(session);
  const playfulTeacher = usePlayfulTeacher();
  const client = useClient();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [regionals, setRegionals] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [classrooms, setClassrooms] = useState([]);

  const grouped = rows.some(r => r.type === GROUP_TYPE);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const load = (page = 1) => {
    setLoading(true);
    return adminLearningPathsApi
      .students(learningPathId, { q: debouncedSearch, page, perPage: 25, ...filters })
      .then(json => {
        const payload = json?.data || [];
        setRows(payload.map(d => ({ id: d.id, type: d.type, ...d.attributes })));
        const meta = json?.meta || {};
        setTrilhaName(meta.learning_path?.name || '');
        setPagination({
          currentPage: meta.current_page || 1,
          totalPages: meta.total_pages || 1,
          totalCount: meta.total_count || payload.length
        });
      })
      .catch(() => {
        toast.error('Erro ao carregar alunos da trilha');
        setRows([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningPathId, debouncedSearch, filters]);

  // Opções dos selects: carregadas uma vez e independentes entre si (escolher um filtro não muda as opções dos outros).
  useEffect(() => {
    client.fetch('regionals').then(res => setRegionals(res?.data || []));
    client.fetch('institutions').then(res => setInstitutions(res?.data || []));
    client.fetch('courses/simple').then(res => setCourses(res?.data || []));
    client.fetch('classrooms/simple').then(res => setClassrooms(res?.data || []));
  }, [client]);

  const changeFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value ?? '' }));

  const openStudentTrails = r => navigate(PATHS.TRILHAS_ADMIN.STUDENT_TRAILS(learningPathId, r.user_id), { state: { userName: r.user_name } });

  const breadcrumbs = {
    title: trilhaName || 'Alunos da trilha',
    nav: [
      { route: PATHS.TRILHAS_ADMIN.LIST, name: teacherOnly ? 'Trilhas' : 'Gerenciar trilhas' },
      { name: 'Alunos', isActive: true }
    ]
  };

  const renderTrailRow = r => (
    <TableRow key={r.id}>
      <TableCell>
        <TitleCell
          title={r.user_name}
          subtitle={r.user_email}
          playful={playfulTeacher}
        />
      </TableCell>
      <TableCell>
        <StatusBadge
          status={r.status}
          playful={playfulTeacher}
        />
      </TableCell>
      <TableCell className="text-sm tabular-nums">{formatPercent(r.percent_walked)}</TableCell>
      <TableCell className="text-sm tabular-nums">{formatPercent(r.percent_mastered)}</TableCell>
      <TableCell className="text-sm tabular-nums">{formatDate(r.generated_at)}</TableCell>
      <TableCell className="text-sm tabular-nums">{formatDate(r.last_accessed_at)}</TableCell>
      <TableCell className="whitespace-nowrap">
        <TrailActions
          playful={playfulTeacher}
          onProgress={() => setProgressTarget(r)}
          onPerformance={() => setPerformanceTarget(r)}
        />
      </TableCell>
    </TableRow>
  );

  const renderGroupRow = r => (
    <TableRow key={r.id}>
      <TableCell>
        <TitleCell
          title={r.user_name}
          subtitle={r.user_email}
          playful={playfulTeacher}
        />
      </TableCell>
      <TableCell className="text-sm tabular-nums">{r.trails_count ?? 0}</TableCell>
      <TableCell className="text-sm tabular-nums">{r.completed_trails_count ?? 0}</TableCell>
      <TableCell className="text-sm tabular-nums">{formatDate(r.last_accessed_at)}</TableCell>
      <TableCell className="whitespace-nowrap">
        {playfulTeacher ? (
          <div className="flex flex-nowrap items-center justify-end gap-2">
            <PlayfulCardButton
              variant="outline"
              icon={FiList}
              className="h-7 w-auto px-4 text-sm"
              onClick={() => openStudentTrails(r)}
            >
              Ver trilhas
            </PlayfulCardButton>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => openStudentTrails(r)}
          >
            <ButtonIcon>
              <FiList />
            </ButtonIcon>{' '}
            Ver trilhas
          </Button>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {/* Playful (professor): banner no padrão da lista de Desafio ao Vivo. */}
      {playfulTeacher && (
        <div className="relative mb-6 sm:mt-16">
          <TiaBanner
            className="h-full"
            image={PENSANDO}
            imageAlt="Personagem do professor"
            imageHeight={190}
            imageOffset={150}
            bubbleCorner="tl"
            bubbleMaxWidth={520}
            text="Acompanhe o progresso da trilha e desempenho por matéria dos seus alunos! 📈"
          />
        </div>
      )}

      <Filter variant="plain">
        <FilterContent>
          <FilterRow className="min-w-[280px]">
            <Label htmlFor="student-search">Buscar aluno</Label>
            <Input
              id="student-search"
              type="search"
              placeholder="Nome ou e-mail"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </FilterRow>
          <FilterSelect
            label="GRE"
            blankLabel="Todas"
            searchable
            value={filters.regionalId}
            onValueChange={v => changeFilter('regionalId', v)}
            options={regionals}
          />
          <FilterSelect
            label="Instituição"
            blankLabel="Todas"
            searchable
            value={filters.institutionId}
            onValueChange={v => changeFilter('institutionId', v)}
            options={institutions}
          />
          <FilterSelect
            label="Curso"
            blankLabel="Todos"
            searchable
            value={filters.courseId}
            onValueChange={v => changeFilter('courseId', v)}
            options={courses}
          />
          <FilterSelect
            label="Turma"
            blankLabel="Todas"
            searchable
            value={filters.classroomId}
            onValueChange={v => changeFilter('classroomId', v)}
            options={classrooms}
          />
        </FilterContent>
      </Filter>

      {loading ? (
        <Loader />
      ) : (
        <>
          <p className="mb-2 text-sm text-neutral">{pagination.totalCount} aluno(s) iniciaram esta trilha.</p>
          {rows.length === 0 ? (
            <EmptyState
              type="data"
              variant="plain"
            />
          ) : (
            <Table className={grouped ? 'min-w-[960px]' : 'min-w-[1320px]'}>
              <TableHeaderWrapper tableHead={grouped ? GROUP_TABLE_HEAD : TABLE_HEAD} />
              <TableBody>{rows.map(r => (r.type === GROUP_TYPE ? renderGroupRow(r) : renderTrailRow(r)))}</TableBody>
            </Table>
          )}

          <Pagination
            pagination={pagination}
            onChange={load}
            playful={playfulTeacher}
          />
        </>
      )}

      <TrilhaProgressModal
        student={progressTarget}
        open={Boolean(progressTarget)}
        onOpenChange={isOpen => {
          if (!isOpen) setProgressTarget(null);
        }}
      />

      <TrilhaPerformanceModal
        student={performanceTarget}
        open={Boolean(performanceTarget)}
        onOpenChange={isOpen => {
          if (!isOpen) setPerformanceTarget(null);
        }}
      />
    </DashboardContent>
  );
}
